import React, { useState } from 'react';
import { Send, Mail, MapPin, Phone, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

const Contact = ({ className = 'py-24 lg:py-32' }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Lengkapi Data', description: 'Mohon isi semua field.' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/contact', {
        name: form.name,
        email: form.email,
        message: form.message,
      });
      toast({
        title: 'Pesan Terkirim!',
        description: 'Tim kami akan menghubungi Anda segera.',
      });
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      toast({
        title: 'Pesan Gagal Terkirim',
        description: err?.response?.data?.detail || 'Terjadi kesalahan. Mohon kirim via WhatsApp atau email.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'support@werdimedia.com' },
    { icon: Phone, label: 'Telepon', value: '+62 851-831-78762' },
    { icon: MapPin, label: 'Alamat', value: 'Bali, Indonesia' },
  ];

  return (
    <section id="contact" className={`relative ${className} bg-[#080808]`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          {/* Left */}
          <div>
            <div className="space-y-5 mb-10">
              {contactInfo.map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D4A94F]/10 border border-[#D4A94F]/25 flex items-center justify-center flex-shrink-0">
                    <c.icon className="w-5 h-5 text-[#E9C46A]" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">
                      {c.label}
                    </p>
                    <p className="text-white font-medium">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <a
              href="https://wa.me/6285183178762"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[#25D366] hover:bg-[#20b858] text-white font-semibold btn-transition"
            >
              <MessageCircle className="w-5 h-5" />
              Chat WhatsApp
            </a>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="bg-[#0d0d0d] border border-white/[0.06] rounded-3xl p-8 md:p-10"
          >
            <div className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2">
                  Nama Lengkap
                </label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nama artis atau nama Anda"
                  className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] focus-visible:border-[#D4A94F] text-white h-12 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2">
                  Email
                </label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="email@example.com"
                  className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] focus-visible:border-[#D4A94F] text-white h-12 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2">
                  Pesan
                </label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Ceritakan tentang project musik Anda..."
                  rows={5}
                  className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] focus-visible:border-[#D4A94F] text-white rounded-xl resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-13 py-3 rounded-xl bg-gradient-to-r from-[#E9C46A] to-[#D4A94F] text-black font-semibold text-base btn-transition gold-shimmer hover:shadow-[0_10px_40px_-10px_rgba(212,169,79,0.5)]"
              >
                {submitting ? 'Kirim...' : 'Kirim Pesan'}
                <Send className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
